import { useEffect, useState } from 'react';
import { dateWithNoTimeDashed } from './date';
const startOfToday = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
};
const msUntilNextMidnight = () => {
    const now = new Date();
    const nextMidnight = new Date(now);
    nextMidnight.setHours(24, 0, 0, 0);
    return nextMidnight.getTime() - now.getTime();
};
/**
 * Web variant of {@link useToday}. Returns the current local date (at start of
 * day) and keeps it fresh as the calendar day changes.
 *
 * It refreshes on two triggers:
 *  1. The browser tab being brought back to the foreground
 *     (`visibilitychange` / window `focus`) — covers a tab left hidden across
 *     midnight.
 *  2. A self-rescheduling timer firing at the next local midnight — covers a
 *     tab left visible across the day boundary.
 *
 * The returned Date's identity only changes when the day actually rolls over,
 * so it is safe to use as a dependency / memo input.
 */
export function useToday() {
    const [today, setToday] = useState(startOfToday);
    useEffect(() => {
        const refresh = () => {
            setToday(prev => {
                const next = startOfToday();
                return dateWithNoTimeDashed(prev) === dateWithNoTimeDashed(next)
                    ? prev
                    : next;
            });
        };
        const handleForeground = () => {
            if (typeof document !== 'undefined' &&
                document.visibilityState !== 'visible') {
                return;
            }
            refresh();
        };
        let timer;
        const scheduleMidnight = () => {
            timer = setTimeout(() => {
                refresh();
                scheduleMidnight();
            }, msUntilNextMidnight());
        };
        scheduleMidnight();
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleForeground);
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('focus', handleForeground);
        }
        return () => {
            clearTimeout(timer);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', handleForeground);
            }
            if (typeof window !== 'undefined') {
                window.removeEventListener('focus', handleForeground);
            }
        };
    }, []);
    return today;
}
